import { useIsSlow } from "@/hooks/useIsSlow";
import { useMobileHeader } from "@/hooks/useMobileHeader";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useListings } from "@/contexts/listings-provider";
import { useTeams } from "@/contexts/teams-provider";
import { t } from "i18next";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import ListingsList from "./listing/listings-list";

export default function ListingMobilePage() {
  const {
    userListings,
    assignedListings,
    shaarpListings,
    apiFetchListings,
    apiFetchAssignedListings,
    apiFetchLoading,
  } = useListings();
  const { teamSelected } = useTeams();

  const isAdminSupportMode = teamSelected?.external === true;
  const personalTabLabel = isAdminSupportMode
    ? t("listing.page.tabs.personalAdminSupport")
    : t("listing.page.tabs.personal");

  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    const init = async () => {
      await apiFetchListings();
      await apiFetchAssignedListings();
    };
    init();
    // Read once on mount: back online, read again, so a list that failed
    // offline fills in as the connection banner says it will.
    window.addEventListener("online", init);
    return () => window.removeEventListener("online", init);
  }, []);
  const isConnectionSlow = useIsSlow(apiFetchLoading);

  const calculateRelevanceScore = (
    listing: (typeof userListings)[0],
    query: string
  ) => {
    const title = listing.title?.toLowerCase() || "";
    const description = listing.description?.toLowerCase() || "";
    const authorName = listing.author?.fullName?.toLowerCase() || "";

    let score = 0;

    // Le titre a le poids le plus élevé
    if (title.includes(query)) {
      score += title.startsWith(query) ? 100 : 50;
    }

    // La description a un poids moyen
    if (description.includes(query)) {
      score += 20;
    }

    // Le nom de l'auteur a le poids le plus faible
    if (authorName.includes(query)) {
      score += 10;
    }

    return score;
  };

  const filterAndSortAllListings = () => {
    if (!searchQuery) {
      return [];
    }

    const query = searchQuery.toLowerCase();
    const allListings = [
      ...userListings,
      ...assignedListings,
      ...shaarpListings,
    ];

    return allListings
      .map((listing) => ({
        listing,
        score: calculateRelevanceScore(listing, query),
      }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((item) => item.listing);
  };

  useMobileHeader({ title: t("listing.page.title"), isConnectionSlow });

  return (
    <div
      data-mobile-scroller
      className="overflow-auto overscroll-y-contain absolute top-0 left-0 flex-1 p-2 pt-2 pb-[calc(var(--mobile-bottom-inset)+1rem)] space-y-5 w-full h-full"
    >
      <div className="space-y-2">
        <div className="flex-1 p-1 pt-0 space-y-5">
          <div className="relative">
            <Input
              placeholder={t("listing.page.searchPlaceholder")}
              className="pr-8 w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 transition-colors -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {searchQuery ? (
            <div className="space-y-2">
              {filterAndSortAllListings().length === 0 ? (
                <div className="flex flex-col justify-center items-center py-8 w-full">
                  <p className="text-sm text-center text-muted-foreground">
                    {t("listing.page.noResults")}
                  </p>
                </div>
              ) : (
                <ListingsList
                  listings={filterAndSortAllListings()}
                  isLoading={apiFetchLoading}
                />
              )}
            </div>
          ) : (
            <Tabs defaultValue="personal">
              <TabsList className="flex gap-2 w-full h-auto">
                <TabsTrigger
                  value="personal"
                  className="flex flex-col justify-center items-center w-full"
                >
                  <span className="text-xs sm:text-sm">
                    {personalTabLabel}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {userListings.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="assigned"
                  className="flex flex-col justify-center items-center w-full"
                >
                  <span className="text-xs sm:text-sm">
                    {t("listing.page.tabs.assigned")}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {assignedListings.length}
                  </Badge>
                </TabsTrigger>
                {shaarpListings.length > 0 && (
                  <TabsTrigger
                    value="shaarp"
                    className="flex flex-col justify-center items-center w-full"
                  >
                    <span className="text-xs sm:text-sm">
                      {t("listing.page.tabs.shaarp")}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {shaarpListings.length}
                    </Badge>
                  </TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="personal" className="space-y-2">
                <ListingsList
                  listings={userListings}
                  isLoading={apiFetchLoading}
                />
              </TabsContent>
              <TabsContent value="assigned">
                <ListingsList
                  listings={assignedListings}
                  isLoading={apiFetchLoading}
                />
              </TabsContent>
              <TabsContent value="shaarp">
                <ListingsList
                  listings={shaarpListings}
                  isLoading={apiFetchLoading}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
    </div>
  );
}
